import { MouseEvent, RefObject, useRef } from "react";
import CustomButton from "../CustomButton";
import { ActionMenuItem } from "./actionMenu.interface";
import ActionMenuTooltip from "./ActionMenuTooltip";
import ActionMenuKindIndicator from "./ActionMenuKindIndicator";
import { actionMenuKindIcons } from "./actionMenuKindConfig";

interface ActionMenuButtonProps {
  item: ActionMenuItem;
  menuRef: RefObject<HTMLElement>;
  tooltipOpen: boolean;
  onTooltipOpen: () => void;
  onTooltipClose: () => void;
  disabled?: boolean;
  onSelect: (
    item: ActionMenuItem,
    source: HTMLElement,
    event: MouseEvent<HTMLElement>,
  ) => void;
}

function ActionMenuButton({
  item,
  menuRef,
  tooltipOpen,
  onTooltipOpen,
  onTooltipClose,
  disabled,
  onSelect,
}: ActionMenuButtonProps) {
  const itemRef = useRef<HTMLSpanElement | null>(null);
  const canNavigate = item.navigate && !item.withConfirmationModal && !disabled;
  const isNeutralColor =
    !item.color || item.color === "default" || item.color === "inherit";
  const leadingIcon =
    item.kind && item.icon === actionMenuKindIcons[item.kind]
      ? "DOCUMENTS"
      : item.icon;

  return (
    <ActionMenuTooltip
      title={item.tooltip}
      menuRef={menuRef}
      itemRef={itemRef}
      open={tooltipOpen}
      onOpen={onTooltipOpen}
      onClose={onTooltipClose}>
      <span
        ref={itemRef}
        className="inline-flex"
        tabIndex={disabled && item.tooltip ? 0 : undefined}
        aria-disabled={disabled || undefined}>
        <CustomButton
          icon={leadingIcon}
          variant="outlined"
          color={
            item.color && item.color !== "default" ? item.color : "inherit"
          }
          sx={{
            ...(isNeutralColor
              ? { color: "text.primary", borderColor: "divider" }
              : {}),
            fontWeight: 500,
            lineHeight: 1.5,
            borderRadius: "6px",
            textAlign: "left",
            whiteSpace: "nowrap",
            "&:hover, &.Mui-focusVisible": {
              backgroundColor: "#F1F5F9",
              ...(isNeutralColor ? { borderColor: "text.secondary" } : {}),
            },
          }}
          navigate={canNavigate ? item.navigate : undefined}
          disabled={disabled}
          loading={item.loading}
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
              return;
            }
            onSelect(item, event.currentTarget, event);
          }}>
          {item.label}
          <ActionMenuKindIndicator kind={item.kind} />
        </CustomButton>
      </span>
    </ActionMenuTooltip>
  );
}

export default ActionMenuButton;
